import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

export interface LearningState {
  path: string;
  type: 'practice' | 'notes' | 'study' | 'mock';
  title: string;
  position?: number;
  topicId?: string;
  topicName?: string;
  timestamp: number;
}

const STORAGE_KEY = "continue_learning_state";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const useContinueLearning = () => {
  const location = useLocation();
  const [learningState, setLearningState] = useState<LearningState | null>(null);

  // Load learning state on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: LearningState = JSON.parse(stored);
        // Check if state is not too old
        if (Date.now() - parsed.timestamp < MAX_AGE_MS) {
          setLearningState(parsed);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.error("Failed to parse learning state", e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Update learning state based on current location
  const updateLearningState = (state: Partial<LearningState>) => {
    const newState: LearningState = {
      path: state.path || location.pathname,
      type: state.type || 'study',
      title: state.title || 'Study Hub',
      position: state.position,
      topicId: state.topicId,
      topicName: state.topicName,
      timestamp: Date.now(),
    };

    setLearningState(newState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  };

  const clearLearningState = () => {
    setLearningState(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const getFormattedState = (): string | null => {
    if (!learningState) return null;

    const parts: string[] = [];
    
    if (learningState.topicName) {
      parts.push(learningState.topicName);
    }

    switch (learningState.type) {
      case 'practice':
        if (learningState.position !== undefined) {
          parts.push(`MCQ ${learningState.position + 1}`);
        } else {
          parts.push('Practice');
        }
        break;
      case 'notes':
        parts.push('Notes');
        break;
      case 'mock':
        parts.push('Mock Exam');
        break;
      case 'study':
        parts.push('Study');
        break;
    }

    return parts.length > 0 ? parts.join(' • ') : learningState.title;
  };

  return {
    learningState,
    updateLearningState,
    clearLearningState,
    getFormattedState,
    hasLearningState: !!learningState,
  };
};
